/**
 * Sidebar — navigation grouped by constitutional role.
 *
 * Each item lists the roles that can access it (per KRCL §4). Items
 * outside the current user's role show as disabled rather than hidden —
 * this makes the org structure visible and helps coordinators understand
 * what other roles see.
 */
import {
  BarChart3,
  Calendar,
  ClipboardList,
  FileText,
  GalleryHorizontalEnd,
  Image as ImageIcon,
  Landmark,
  LayoutDashboard,
  PoundSterling,
  ScrollText,
  Settings,
  Users,
  Users2,
} from "lucide-react";
import { NavLink } from "react-router-dom";

import { useAuth, type Role } from "../auth/AuthProvider";
import { cn } from "../lib/cn";

interface NavItem {
  to: string;
  label: string;
  icon: typeof LayoutDashboard;
  roles: Role[];
}

const NAV: NavItem[] = [
  { to: "/dashboard",     label: "Dashboard",     icon: LayoutDashboard, roles: ["coordinator", "secretary", "treasurer", "editor", "member"] },
  { to: "/members",       label: "Members",       icon: Users,           roles: ["coordinator", "secretary"] },
  { to: "/payments",      label: "Payments",      icon: PoundSterling,   roles: ["coordinator", "treasurer"] },
  { to: "/plans",         label: "Plans",         icon: ClipboardList,   roles: ["coordinator", "treasurer"] },
  { to: "/content/posts", label: "Posts",         icon: FileText,        roles: ["coordinator", "secretary", "editor"] },
  { to: "/content/pages", label: "Pages",         icon: ScrollText,      roles: ["coordinator", "secretary", "editor"] },
  { to: "/events",        label: "Events",        icon: Calendar,        roles: ["coordinator", "secretary"] },
  { to: "/banners",       label: "Banners",       icon: GalleryHorizontalEnd, roles: ["coordinator", "secretary", "editor"] },
  { to: "/gallery",       label: "Gallery",       icon: ImageIcon,       roles: ["coordinator", "secretary", "editor"] },
  { to: "/committee",     label: "Committee",     icon: Users2,          roles: ["coordinator"] },
  { to: "/settings",      label: "Site settings", icon: Settings,        roles: ["coordinator"] },
  { to: "/audit",         label: "Audit log",     icon: BarChart3,       roles: ["coordinator"] },
  { to: "/users",         label: "Users",         icon: Landmark,        roles: ["coordinator"] },
];

export function Sidebar() {
  const { user, hasRole } = useAuth();

  return (
    <aside className="flex w-64 shrink-0 flex-col border-r border-stone-200 bg-white">
      <div className="flex items-center gap-3 border-b border-stone-200 px-5 py-4">
        <div
          aria-hidden
          className="size-9 rounded-md bg-flag-red"
          style={{
            background:
              "linear-gradient(135deg, var(--color-flag-red) 0%, var(--color-flag-red) 33%, var(--color-flag-white) 33%, var(--color-flag-white) 66%, var(--color-flag-green) 66%, var(--color-flag-green) 100%)",
          }}
        />
        <div>
          <div className="text-sm font-semibold leading-tight">KRCL Admin</div>
          <div className="text-xs text-stone-500">Kurdish Rojava Liverpool</div>
        </div>
      </div>

      <nav className="flex-1 space-y-1 overflow-y-auto px-3 py-4">
        {NAV.map((item) => {
          const allowed = hasRole(...item.roles);
          const Icon = item.icon;
          return (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) =>
                cn(
                  "group flex items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors",
                  isActive
                    ? "bg-red-50 text-red-700"
                    : allowed
                      ? "text-stone-700 hover:bg-stone-100 hover:text-stone-900"
                      : "cursor-not-allowed text-stone-400",
                )
              }
              onClick={(e) => {
                if (!allowed) e.preventDefault();
              }}
              title={!allowed ? `Restricted to: ${item.roles.join(", ")}` : undefined}
            >
              <Icon className="size-4 shrink-0" />
              <span>{item.label}</span>
            </NavLink>
          );
        })}
      </nav>

      {user && (
        <div className="border-t border-stone-200 px-5 py-3 text-xs text-stone-500">
          Signed in as <span className="text-stone-700">{user.email}</span>
          <div className="mt-0.5 capitalize text-stone-400">{user.role}</div>
        </div>
      )}
    </aside>
  );
}
